import { Tag } from 'antd';
import { Price } from 'components/MarketItem/Details/Price';
import Favorite from 'components/MarketItem/Favorite';
import { TotalQuantity } from 'components/MarketItem/QuantityInput/QuantityInput';
import { FrameImage } from 'components/Product/ProductImagesModal';
import ProductNote, { NoteSm } from 'components/ProductNote';
import { Viewport } from 'components/ViewportProvider';
import { selectAddedProducts, selectAddingProducts, selectUserPermissions } from 'containers/MainLayout/selectors';
import { getImageByCategory } from 'containers/MarketPage/ImageByCategory';
import QuantityInput from 'containers/QuantityInput';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { Category, Product, ProductNoteInput, TrackingAddToCartAddFromType } from 'types/schema';
import { allowDecimalQuantity } from 'utils/allowDecimalQuantity';
import generateProductSlug from 'utils/generateProductSlug';
import utilsMessages from 'utils/messages';

const FavoriteItemDesktopContainer = styled.div`
  padding: ${({ spacing }) => (spacing ? '10px 8px' : '16px 24px')};
  background-color: white;
  display: flex;
  gap: ${({ spacing }) => (spacing ? '8px' : '50px')};
  flex-wrap: nowrap;
  flex: 1;
  align-items: center;
  font-size: 16px;
  &:hover {
    background-color: #fff;
  }

  @media (max-width: 1024px) {
    flex-wrap: wrap;
    min-height: 124px;
    align-items: flex-start;
  }
`;

const FavoriteTitle = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  a {
    color: rgba(0, 0, 0, 0.87);
  }
`;

const FavoriteActions = styled.div`
  margin-top: 2px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  position: relative;
  flex-grow: 1;
  flex-basis: 16px;

  ${TotalQuantity} {
    flex-basis: 100%;
    justify-content: flex-end;
  }
`;

const FavoriteContent = styled.div`
  width: calc(100% - 90px);
  display: flex;
  flex-direction: column;
  font-size: 14px;
`;

const FavoritePrice = styled.div`
  margin: 4px 0;
  font-weight: 300;
  color: #595959;
`;

const StyledLink = styled(Link)`
  color: rgba(0, 0, 0, 0.87);
  &:hover {
    color: #40a9ff;
  }
`;

const componentPermissions = {
  addToCartPermission: 'ORDER_PLACE',
  addFavoritePermission: 'BUYER_FAVORITE_ITEM_ADD',
};

interface FavoriteItemProps {
  permissions: string[];
  onOpenPreviewImage: () => void;
  onToggleFavorite: () => void;
  onOpenNote: () => void;
  setProductNote: (data: ProductNoteInput) => void;
  deleteNote: () => void;
  index: number;
  product: Product;
  baseUom: string;
  netUom: string;
}

interface FavoriteItemWithTrackingProps {
  id: string;
  price: number;
  category?: Category | null;
  spacing: boolean;
}

const FavoriteItemWithTracking: FC<FavoriteItemWithTrackingProps> = (props) => {
  const { children, spacing } = props;

  return <FavoriteItemDesktopContainer spacing={spacing}>{children}</FavoriteItemDesktopContainer>;
};

const FavoriteItemMobile: FC<FavoriteItemProps> = (props: FavoriteItemProps) => {
  const { onOpenPreviewImage, onToggleFavorite, onOpenNote, deleteNote, permissions, product, baseUom, netUom } = props;
  const { inStock, imageUrl, name, note, uom, category, supplierInternalCode, tagImageEn, tagImageVn } = product;
  const lang = localStorage.getItem('lang') || 'vi';
  const frameTagImage = lang === 'vi' ? tagImageVn : tagImageEn || '';

  return (
    <>
      <div style={{ position: 'relative' }}>
        <ImageContainer height={80} onClick={onOpenPreviewImage}>
          <img src={imageUrl || getImageByCategory(category)} alt={String(name)} width={80} height={80} />
          {frameTagImage && <FrameImage src={frameTagImage} />}
        </ImageContainer>
        {permissions.indexOf(componentPermissions.addFavoritePermission) > -1 && (
          <div style={{ position: 'absolute', left: 0, top: 80, transform: 'translateY(-50%)' }}>
            <Favorite favorite="true" position="static" onClick={onToggleFavorite} />
          </div>
        )}
      </div>
      <FavoriteContent>
        <div>
          <FavoriteTitle>
            <Link to={`/products/${generateProductSlug(name, supplierInternalCode, lang)}`}>{name}</Link>
          </FavoriteTitle>
          <FavoritePrice>
            <Price product={product} baseUom={baseUom} netUom={netUom} />
          </FavoritePrice>
          {note && <NoteSm>{note}</NoteSm>}
        </div>
        <FavoriteActions>
          <ProductNote note={note} isShowNote={false} onClickOpenNote={onOpenNote} />
          {permissions.indexOf(componentPermissions.addToCartPermission) > -1 &&
            (inStock ? (
              <QuantityInput
                product={product}
                size="small"
                allowDecimal={allowDecimalQuantity(uom)}
                addFrom={TrackingAddToCartAddFromType.FavoriteProduct}
              />
            ) : (
              <Tag
                style={{
                  background: '#efefef',
                  border: 'none',
                  borderRadius: 50,
                  color: '#8c8c8c',
                  fontWeight: 500,
                  marginTop: 10,
                  marginRight: 0,
                  padding: '7px 12px',
                  float: 'right',
                  fontSize: 16,
                  width: 152,
                  textAlign: 'center',
                }}
              >
                <FormattedMessage {...utilsMessages.outStock} />
              </Tag>
            ))}
        </FavoriteActions>
      </FavoriteContent>
    </>
  );
};

const StatusIconContainer = styled.div`
  position: relative;
  z-index: 0;
`;
const ProductNameContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const ProductPriceContainer = styled.div`
  width: 200px;
`;

const ActionContainer = styled.div`
  max-width: 172px;
  width: 100%;
  text-align: right;
`;

const ImageContainer = styled.div`
  display: inline-block;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  ${({ height }) =>
    height &&
    `
    height: ${height}px;
  `}
`;

const FavoriteItemDesktop: FC<FavoriteItemProps> = (props: FavoriteItemProps) => {
  const { onOpenPreviewImage, onToggleFavorite, onOpenNote, deleteNote, permissions, product, baseUom, netUom } = props;
  const { inStock, imageUrl, name, note, uom, category, supplierInternalCode, tagImageVn, tagImageEn } = product;
  const lang = localStorage.getItem('lang') || 'vi';
  const frameTagImage = lang === 'vi' ? tagImageVn : tagImageEn || '';

  return (
    <>
      <StatusIconContainer>
        {permissions.indexOf(componentPermissions.addFavoritePermission) > -1 && (
          <Favorite favorite="true" position="relative" alignto="auto" onClick={onToggleFavorite} />
        )}
      </StatusIconContainer>
      <ImageContainer height={90} onClick={onOpenPreviewImage}>
        <img src={imageUrl || getImageByCategory(category)} alt={String(name)} width={90} height={90} />
        {frameTagImage && <FrameImage src={frameTagImage} />}
      </ImageContainer>
      <ProductNameContainer>
        <StyledLink to={`/products/${generateProductSlug(name, supplierInternalCode, lang)}`}>{name}</StyledLink>
        <ProductNote note={note} onClickOpenNote={onOpenNote} />
      </ProductNameContainer>
      <ProductPriceContainer>
        <Price product={product} baseUom={baseUom} netUom={netUom} />
      </ProductPriceContainer>
      <ActionContainer>
        {permissions.indexOf(componentPermissions.addToCartPermission) > -1 &&
          (inStock ? (
            <QuantityInput
              product={product}
              size="large"
              allowDecimal={allowDecimalQuantity(uom)}
              addFrom={TrackingAddToCartAddFromType.FavoriteProduct}
            />
          ) : (
            <Tag
              style={{
                background: '#efefef',
                border: 'none',
                borderRadius: 50,
                color: '#8c8c8c',
                fontWeight: 500,
                marginTop: 10,
                marginRight: 0,
                padding: '7px 12px',
                float: 'right',
                fontSize: 16,
                width: 152,
                textAlign: 'center',
              }}
            >
              <FormattedMessage {...utilsMessages.outStock} />
            </Tag>
          ))}
      </ActionContainer>
    </>
  );
};

interface RootState {}
interface StateProps {}

const FavoriteItem: FC<FavoriteItemProps> = (props: FavoriteItemProps) => {
  const { product } = props;
  const { baseUom, netUom } = useProductUomInfo(product);
  const { id, price, category } = product;

  return (
    <Viewport.Consumer>
      {({ width }) => (
        <FavoriteItemWithTracking id={id} price={price} category={category} spacing={width < 1025}>
          {width < 1025 ? (
            <FavoriteItemMobile {...props} baseUom={baseUom} netUom={netUom} />
          ) : (
            <FavoriteItemDesktop {...props} baseUom={baseUom} netUom={netUom} />
          )}
        </FavoriteItemWithTracking>
      )}
    </Viewport.Consumer>
  );
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  addedProducts: selectAddedProducts(),
  addingProducts: selectAddingProducts(),
  permissions: selectUserPermissions(),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const added = stateProps.addedProducts[ownProps.id];
  const adding = stateProps.addingProducts[ownProps.id];
  return {
    ...ownProps,
    added: added,
    adding: adding,
    permissions: stateProps.permissions,
  };
};

const withConnect = connect(mapStateToProps, null, mergeProps);

export default withConnect(FavoriteItem);
